import React, { useCallback, useState } from 'react';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import { validateAllInputs, validateTenantSelected } from './validation';
import type { ILeasesForPropertyModel } from '@rental-manager/models/Types';
import { detectCombinedNames } from '@common/utils/detectCombinedNames';
import errorMessages from '@common/constants/errorMessages';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import {
  Box,
  Button,
  FormField,
  FormHelp,
  Input,
  Label,
  Paragraph,
  Select,
  TextButton,
} from '@zillow/constellation-10';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import userPersonas from '@common/constants/userPersonas';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  fromDashboard: boolean;
  tenantFirstName: string;
  tenantLastName: string;
  emailAddress: string;
  tenantId: number;
  editMode: boolean;
  collectMoveInCosts: boolean;
  onSubmit: ({ tenantFirstName, tenantLastName, emailAddress, tenantId }) => Promise<any>;
  onBack: ({ tenantFirstName, tenantLastName, emailAddress }) => void;
  onClose: () => void;
  onCancel: () => void;
  selectedLease: ILeasesForPropertyModel;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}

const PRIMARY_TENANT_ENTER_HELP_TEXT = 'Only enter the information of your primary tenant.';

const defaultProps = {
  fromDashboard: false,
  tenantId: -1,
  currentTenantEmail: null,
  editMode: false,
  collectMoveInCosts: false,
};

const InviteTenantPage: React.FC<Props> = (props) => {
  const {
    fromDashboard,
    tenantFirstName: propTenantFirstName,
    tenantLastName: propTenantLastName,
    emailAddress: propEmailAddress,
    tenantId: propTenantId,
    editMode,
    collectMoveInCosts,
    onSubmit,
    onBack,
    onClose,
    onCancel,
    selectedLease,
    currentProperty,
    user,
  } = { ...defaultProps, ...props };

  const isSingleTenantOnLease = selectedLease?.leaseTenants?.length === 1;
  const firstName = propTenantFirstName || selectedLease?.leaseTenants?.[0]?.firstName;
  const lastName = propTenantLastName || selectedLease?.leaseTenants?.[0]?.lastName;

  const [tenantFirstName, setTenantFirstName] = useState({
    value: firstName || '',
    error: detectCombinedNames(firstName) ? errorMessages.COMBINED_NAMES_ERROR_TEXT : '',
  });
  const [tenantLastName, setTenantLastName] = useState({
    value: lastName || '',
    error: detectCombinedNames(lastName) ? errorMessages.COMBINED_NAMES_ERROR_TEXT : '',
  });
  const [emailAddress, setEmailAddress] = useState({
    value: propEmailAddress || selectedLease?.leaseTenants?.[0]?.email || '',
    error: '',
  });
  const [tenantId, setTenantId] = useState({
    value: isSingleTenantOnLease ? 0 : propTenantId,
    error: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const onInputChange = useCallback((e) => {
    const key = e.target.dataset.statekey;
    const value = e.target.value;
    if (key === 'tenantFirstName') setTenantFirstName({ value, error: '' });
    else if (key === 'tenantLastName') setTenantLastName({ value, error: '' });
    else if (key === 'emailAddress') setEmailAddress({ value, error: '' });
  }, []);

  const onInputChangeName = useCallback((e) => {
    const key = e.target.dataset.statekey;
    const value = e.target.value;
    let error = '';
    if (detectCombinedNames(value)) {
      error = errorMessages.COMBINED_NAMES_ERROR_TEXT;
    }
    if (key === 'tenantFirstName') setTenantFirstName({ value, error });
    else if (key === 'tenantLastName') setTenantLastName({ value, error });
  }, []);

  const renderTenantOptions = useCallback((): React.ReactNode => {
    const defaultOption = (
      <option key={-1} disabled value="-1">
        Select one
      </option>
    );
    const options = selectedLease?.leaseTenants?.map((leaseTenant, index) => {
      return (
        <option key={index} value={index}>
          {leaseTenant.firstName}&nbsp;{leaseTenant.lastName}
        </option>
      );
    });
    return [defaultOption, ...(options || [])];
  }, [selectedLease]);

  const onChangeSelect = useCallback((e) => {
    const selectedOption = e.currentTarget.options[e.currentTarget.selectedIndex];
    const value = parseInt(selectedOption.getAttribute('value'), 10);
    setTenantId({ value, error: '' });
  }, []);

  const toggleIsLoading = useCallback(
    (state = !isLoading) => {
      setIsLoading(state);
    },
    [isLoading],
  );

  const ctx = React.useMemo(
    () => ({
      state: { tenantFirstName, tenantLastName, emailAddress, tenantId },
      props: { ...props },
      setState: (newState) => {
        if (newState.tenantFirstName) setTenantFirstName(newState.tenantFirstName);
        if (newState.tenantLastName) setTenantLastName(newState.tenantLastName);
        if (newState.emailAddress) setEmailAddress(newState.emailAddress);
        if (newState.tenantId) setTenantId(newState.tenantId);
      },
    }),
    [tenantFirstName, tenantLastName, emailAddress, tenantId, props],
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (validateAllInputs(ctx)) {
        return Promise.resolve()
          .then(() => toggleIsLoading(true))
          .then(() => {
            if (!fromDashboard) {
              IreneOnboardingCSEvents.emitContinueFromInviteTenantPageEvent({
                currentProperty,
                user,
              });
            }
            return onSubmit({
              tenantFirstName: tenantFirstName.value.trim(),
              tenantLastName: tenantLastName.value.trim(),
              emailAddress: emailAddress.value.trim(),
              tenantId: tenantId.value,
            });
          })
          .then((response = {}) => {
            if (response.error) {
              return toggleIsLoading(false);
            }
            return undefined;
          });
      }
      return false;
    },
    [
      fromDashboard,
      editMode,
      currentProperty,
      user,
      onSubmit,
      tenantFirstName,
      tenantLastName,
      emailAddress,
      tenantId,
      toggleIsLoading,
      ctx,
    ],
  );

  const handleSubmitForSignAndCollect = useCallback(
    (e) => {
      e.preventDefault();
      if (validateTenantSelected(ctx)) {
        return Promise.resolve()
          .then(() => toggleIsLoading(true))
          .then(() => {
            if (!fromDashboard) {
              IreneOnboardingCSEvents.emitContinueFromInviteTenantPageEvent({
                currentProperty,
                user,
              });
            }
            return onSubmit({
              tenantFirstName: selectedLease?.leaseTenants[tenantId.value].firstName.trim(),
              tenantLastName: selectedLease?.leaseTenants[tenantId.value].lastName.trim(),
              emailAddress: selectedLease?.leaseTenants[tenantId.value].email.trim(),
              tenantId: tenantId.value,
            });
          })
          .then((response = {}) => {
            if (response.error) {
              return toggleIsLoading(false);
            }
            return undefined;
          });
      }
      return false;
    },
    [fromDashboard, editMode, currentProperty, user, onSubmit, selectedLease, tenantId, toggleIsLoading, ctx],
  );

  const gotoPreviousPage = useCallback(() => {
    IreneOnboardingCSEvents.emitBackFromOnboardingFlowEvent({
      currentProperty,
      triggerObject: 'tenant_info',
      user,
    });
    onBack({
      tenantFirstName: tenantFirstName.value?.trim(),
      tenantLastName: tenantLastName.value?.trim(),
      emailAddress: emailAddress.value?.trim(),
    });
  }, [currentProperty, user, onBack, tenantFirstName, tenantLastName, emailAddress]);

  const handleCancel = useCallback(
    (e) => {
      e.preventDefault();
      onCancel();
    },
    [fromDashboard, editMode, onCancel],
  );

  const handleClose = useCallback(() => {
    if (!fromDashboard) {
      IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
        currentProperty,
        triggerObject: 'tenant_info',
        user,
      });
    }
    onClose();
  }, [fromDashboard, currentProperty, user, onClose]);

  const renderSACVariation = useCallback((): React.ReactNode => {
    return (
      <FormField
        css={{ maxWidth: '36rem' }}
        required
        error={Boolean(tenantId.error)}
        control={
          <Select size="md" value={tenantId.value} onChange={onChangeSelect}>
            {renderTenantOptions()}
          </Select>
        }
        description={tenantId.error && <FormHelp>{tenantId.error}</FormHelp>}
        label={<Label>Select your primary tenant</Label>}
      />
    );
  }, [tenantId, onChangeSelect, renderTenantOptions]);

  const renderDefault = useCallback((): React.ReactNode => {
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateRows: 'auto',
          gap: 'default',
          paddingBottom: 'default',
        }}
      >
        <FormField
          css={{ maxWidth: '36rem' }}
          required
          error={Boolean(tenantFirstName.error)}
          control={
            <Input
              fluid
              size="md"
              type="text"
              onChange={onInputChangeName}
              value={tenantFirstName.value}
              className="fs-exclude"
              data-statekey="tenantFirstName"
            />
          }
          description={tenantFirstName.error && <FormHelp>{tenantFirstName.error}</FormHelp>}
          label={<Label>First name</Label>}
        />
        <FormField
          css={{ maxWidth: '36rem' }}
          required
          error={Boolean(tenantLastName.error)}
          control={
            <Input
              fluid
              size="md"
              type="text"
              onChange={onInputChangeName}
              value={tenantLastName.value}
              className="fs-exclude"
              data-statekey="tenantLastName"
            />
          }
          description={tenantLastName.error && <FormHelp>{tenantLastName.error}</FormHelp>}
          label={<Label>Last name</Label>}
        />
        <FormField
          css={{ maxWidth: '36rem' }}
          required
          error={Boolean(emailAddress.error)}
          control={
            <Input
              fluid
              size="md"
              type="text"
              onChange={onInputChange}
              value={emailAddress.value}
              className="fs-exclude"
              data-statekey="emailAddress"
            />
          }
          description={emailAddress.error && <FormHelp>{emailAddress.error}</FormHelp>}
          label={<Label>Email address</Label>}
        />
      </Box>
    );
  }, [tenantFirstName, tenantLastName, emailAddress, onInputChangeName, onInputChange]);

  const showTenantDropdown = collectMoveInCosts && selectedLease?.leaseTenants?.length > 0;

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      aside={<FaqComponent faqs={FaqConstants.ONBOARDING_V2.INVITE_TENANT_PAGE} />}
      onRequestClose={handleClose}
      header={fromDashboard ? 'Tenant info' : 'Payment setup'}
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          <Box>
            <Paragraph textStyle="body-lg-bold">Who will send you payments?</Paragraph>
            {!showTenantDropdown && <Paragraph textStyle="body-sm">{PRIMARY_TENANT_ENTER_HELP_TEXT}</Paragraph>}
          </Box>
          {showTenantDropdown ? renderSACVariation() : renderDefault()}
        </Box>
      }
      backAction={fromDashboard ? undefined : { onClick: gotoPreviousPage }}
      footerAriaLabel="Tenant invitation actions"
      footerEnd={
        <>
          {(editMode || fromDashboard) && <TextButton onClick={handleCancel}>Cancel</TextButton>}
          <Button
            emphasis="filled"
            loading={isLoading}
            onClick={showTenantDropdown ? handleSubmitForSignAndCollect : handleSubmit}
          >
            {editMode || fromDashboard ? 'Update' : 'Continue'}
          </Button>
        </>
      }
    />
  );
};

InviteTenantPage.defaultProps = defaultProps;

export default InviteTenantPage;
